import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Award, TrendingUp, Users, Languages, ThumbsUp } from 'lucide-react';

// Constants
const ANIMATION_DURATION = 2000;
const ANIMATION_FRAME_RATE = 16;
const INTERSECTION_THRESHOLD = 0.3;

// Static data - hoisted to module scope to prevent re-creation on each render
const STATS_DATA = [
  { id: 'years', icon: TrendingUp, label: 'Years in Business', value: 15, suffix: '+' },
  { id: 'awards', icon: Award, label: 'Awards Won', value: 12, suffix: '+' },
  { id: 'clients', icon: Users, label: 'Clients', value: 5000, suffix: '+' },
  { id: 'languages', icon: Languages, label: 'Languages Spoken', value: 8, suffix: '+' },
  { id: 'satisfaction', icon: ThumbsUp, label: 'Customer Satisfaction', value: 98, suffix: '%' },
];

const AWARDS_DATA = [
  { id: 'excellence', title: 'Business Excellence Award 2023' },
  { id: 'innovative', title: 'Most Innovative Consultancy 2024' },
  { id: 'sme', title: 'SME of the Year 2024' },
];

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState(STATS_DATA.map(() => 0));
  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
        }
      },
      { threshold: INTERSECTION_THRESHOLD }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animateCount = useCallback((end, index) => {
    let start = 0;
    const increment = end / (ANIMATION_DURATION / ANIMATION_FRAME_RATE);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCounts((prev) => {
          const newCounts = [...prev];
          newCounts[index] = end;
          return newCounts;
        });
        clearInterval(timer);
      } else {
        setCounts((prev) => {
          const newCounts = [...prev];
          newCounts[index] = Math.floor(start);
          return newCounts;
        });
      }
    }, ANIMATION_FRAME_RATE);

    return timer;
  }, []);

  useEffect(() => {
    if (isVisible) {
      const timers = STATS_DATA.map((stat, index) => animateCount(stat.value, index));
      return () => timers.forEach(timer => clearInterval(timer));
    }
  }, [isVisible, animateCount]);

  return (
    <section ref={sectionRef} className="py-20 bg-[#0A1929] text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-[#DC143C]">Firmzy</span> By The Numbers
          </h2>
          <p className="text-xl text-gray-300">Our track record speaks for itself</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-20">
          {STATS_DATA.map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={stat.id}
                className="text-center group hover:scale-110 transition-transform duration-300"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:shadow-2xl group-hover:shadow-purple-500/50 transition-all">
                  <IconComponent size={32} />
                </div>
                <div className="text-4xl md:text-5xl font-bold mb-2 text-white">
                  {counts[idx].toLocaleString()}{stat.suffix}
                </div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Awards Section */}
        <div className="border-t border-white/10 pt-16">
          <h3 className="text-3xl font-bold text-center mb-12">Awards & Recognition</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {AWARDS_DATA.map((award) => (
              <div
                key={award.id}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center hover:bg-white/10 transition-all hover:scale-105 border border-white/10"
              >
                <div className="h-24 flex items-center justify-center mb-4">
                  <Award className="text-[#DC143C]" size={64} />
                </div>
                <p className="text-lg font-medium">{award.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
